import { useEffect, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import get from 'lodash/get'
import last from 'lodash/last'
import { observer } from 'mobx-react'
import { MdMailOutline as MailIcon } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { useStores } from 'hooks/useStores'
import { EInboxTab } from 'pages/SupportInboxPage/constants'
import routes from 'routes'
import { getName } from 'utils/users'

dayjs.extend(relativeTime)

interface InboxRow {
  threadId: number
  type: EInboxTab
  senderName: string
  senderImage?: string
  createdAt: string | Date
  isUnread: boolean
  preview: string
}

/**
 * Dark-themed inbox button + dropdown for the RallyPD topbar. Mirrors the
 * legacy MessageButton experience (badge count, thread preview list, jump
 * to /messages) but rebuilt in the new chrome's palette and trimmed to the
 * essentials — full thread filters live on the inbox page itself.
 */
const PlayInboxButton = observer(() => {
  const navigate = useNavigate()
  const { messageStore } = useStores()
  const {
    unreadGeneralThreadCount = 0,
    unreadSupportThreadCount = 0,
    generalMessageThreads,
    supportThreads
  } = messageStore
  const unread = unreadGeneralThreadCount + unreadSupportThreadCount
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    // Refresh thread counts + previews each time the dropdown opens. Mirrors
    // the legacy MessageButton fetch pattern but only on open (avoids the
    // chatty Firebase-driven re-render loop the legacy widget ran on mount).
    Promise.all([
      messageStore.getUnreadThread(),
      messageStore.fetchGeneralMessageThreads(20, '', true),
      messageStore.getSupportMessageThreads(20, true)
    ]).catch(() => {
      // Silently swallow — empty thread list still renders the inbox link.
    })
  }, [isOpen, messageStore])

  const rows: InboxRow[] = []
  for (const t of generalMessageThreads ?? []) {
    if (!t?.id) continue
    const latest = last(t.groupMessages) ?? t.groupMessages?.[0]
    rows.push({
      threadId: t.id,
      type: EInboxTab.GENERAL,
      senderName: getName(latest?.user),
      senderImage: latest?.user?.image ?? '',
      createdAt: latest?.createdAt ?? new Date(),
      isUnread: !t.isSeen,
      preview: 'sent you a message'
    })
  }
  for (const s of supportThreads ?? []) {
    const thread = s?.thread
    if (!thread?.id) continue
    const latest = get(thread, 'supportMessage')
    const sender = get(latest, 'user')
    rows.push({
      threadId: thread.id,
      type: EInboxTab.SUPPORT,
      senderName: getName(sender),
      senderImage: sender?.image ?? '',
      createdAt: latest?.createdAt ?? new Date(),
      isUnread: !s.isSeen,
      preview: thread.step?.name || thread.process?.name || 'replied to your thread'
    })
  }
  rows.sort((a, b) => {
    if (a.isUnread !== b.isUnread) return a.isUnread ? -1 : 1
    return dayjs(b.createdAt).diff(dayjs(a.createdAt))
  })
  const topRows = rows.slice(0, 8)

  const handleRowClick = (row: InboxRow) => {
    if (row.type === EInboxTab.SUPPORT) {
      messageStore.setCurrentSupportThreadId(row.threadId)
    } else {
      messageStore.setCurrentGeneralThreadId(row.threadId)
    }
    navigate(`${routes.messages.value}?tab=${row.type}&threadId=${row.threadId}`)
    setIsOpen(false)
  }

  const handleGoToInbox = () => {
    navigate(routes.messages.value)
    setIsOpen(false)
  }

  return (
    <Menu isOpen={isOpen} onClose={() => setIsOpen(false)} closeOnSelect={false}>
      <MenuButton
        as={IconButton}
        aria-label="Inbox"
        bg="transparent"
        color="#F4F8FB"
        borderRadius="full"
        w="36px"
        h="36px"
        border="none"
        position="relative"
        _hover={{ bg: 'rgba(255,255,255,0.08)' }}
        _active={{ bg: 'rgba(255,255,255,0.12)' }}
        onClick={() => setIsOpen(prev => !prev)}
        icon={
          <Box position="relative">
            <MailIcon size={20} />
            {unread > 0 && (
              <Box
                position="absolute"
                top="-4px"
                right="-6px"
                minW="14px"
                h="14px"
                px="3px"
                borderRadius="full"
                bg="#ef4444"
                color="white"
                fontSize="9px"
                lineHeight="14px"
                textAlign="center"
                fontWeight={600}
              >
                {unread > 99 ? '99+' : unread}
              </Box>
            )}
          </Box>
        }
      />
      <MenuList
        bg="#12203a"
        borderColor="rgba(255,255,255,0.1)"
        color="white"
        maxH="60vh"
        overflowY="auto"
        w="360px"
        p={0}
        boxShadow="0 12px 32px rgba(0,0,0,0.4)"
        sx={{
          '.chakra-menu__menuitem': { outline: 'none', border: 'none' },
          '.chakra-menu__menuitem:focus, .chakra-menu__menuitem:focus-visible, .chakra-menu__menuitem[data-focus]': {
            boxShadow: 'none',
            outline: 'none'
          }
        }}
      >
        <HStack
          justify="space-between"
          px={4}
          py={3}
          borderBottom="1px solid rgba(255,255,255,0.08)"
          position="sticky"
          top={0}
          bg="#12203a"
          zIndex={1}
        >
          <Text fontWeight={600} m={0}>
            Messages
          </Text>
          {unread > 0 && (
            <Text fontSize="xs" color="rgba(244,248,251,0.6)" m={0}>
              {unread} unread
            </Text>
          )}
        </HStack>
        {topRows.length === 0 && (
          <Box px={4} py={6} textAlign="center" opacity={0.6}>
            <Text fontSize="sm" m={0}>
              No messages yet
            </Text>
          </Box>
        )}
        {topRows.map(row => (
          <MenuItem
            key={`${row.type}-${row.threadId}`}
            bg={row.isUnread ? 'rgba(59,130,246,0.08)' : 'transparent'}
            color="white"
            _hover={{ bg: 'rgba(255,255,255,0.04)' }}
            _focus={{ bg: 'rgba(255,255,255,0.04)', boxShadow: 'none' }}
            _focusVisible={{ boxShadow: 'none' }}
            px={3}
            py={2}
            onClick={() => handleRowClick(row)}
          >
            <HStack align="flex-start" w="100%" spacing={2}>
              <Avatar size="sm" name={row.senderName} src={row.senderImage} />
              <VStack align="flex-start" flex={1} minW={0} spacing={0.5}>
                <Text fontSize="sm" fontWeight={row.isUnread ? 600 : 500} m={0} noOfLines={1}>
                  {row.senderName || 'Someone'}
                </Text>
                <Text fontSize="xs" m={0} color="rgba(244,248,251,0.65)" noOfLines={1}>
                  {row.preview}
                </Text>
              </VStack>
              <Text fontSize="xs" opacity={0.5} whiteSpace="nowrap">
                {dayjs(row.createdAt).fromNow(true)}
              </Text>
            </HStack>
          </MenuItem>
        ))}
        <Box px={4} py={3} borderTop="1px solid rgba(255,255,255,0.08)" position="sticky" bottom={0} bg="#12203a">
          <Button
            size="sm"
            w="100%"
            bg="rgba(255,255,255,0.06)"
            color="white"
            border="1px solid rgba(255,255,255,0.12)"
            _hover={{ bg: 'rgba(255,255,255,0.1)' }}
            onClick={handleGoToInbox}
          >
            Go to inbox
          </Button>
        </Box>
      </MenuList>
    </Menu>
  )
})

export default PlayInboxButton
